/**
 * The customer's invoice, at `/i/:token`.
 *
 * The invoice email's button lands here. Nothing is paid or signed on this
 * page; it exists so the office can know the customer looked, which an
 * attachment can never tell anyone. What it draws is the server's
 * `buildInvoiceModel` — the same words the PDF prints — on the proposal's
 * paper, and the copy the email carried is one tap away.
 *
 * `invoicePortal.opened` is sent once, on mount, and never from the query: a
 * refetch is not a second visit. Decision 88.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Icon } from '../../components/Icon.js'
import { Letterhead, themeOf, themeVars } from '../../components/proposal/Letterhead.js'
import { Spinner } from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { apiOrigin, errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

type View = RouterOutputs['invoicePortal']['view']

function absolute(path: string | null): string | null {
  if (!path) return null
  return /^https?:/.test(path) ? path : `${apiOrigin}${path}`
}

export function InvoiceView() {
  const { token = '' } = useParams()
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.invoicePortal.view.queryOptions({ token }),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const opened = useMutation(trpc.invoicePortal.opened.mutationOptions())
  const sentOpen = useRef(false)
  useEffect(() => {
    if (sentOpen.current || !query.data) return
    sentOpen.current = true
    opened.mutate({ token })
  }, [query.data, opened, token])

  useEffect(() => {
    const name = query.data?.business.name
    document.title = name ? `Invoice from ${name}` : 'Your invoice'
  }, [query.data?.business.name])

  if (query.isPending) {
    return (
      <Screen>
        <div className="grid min-h-[60vh] place-items-center">
          <Spinner className="size-6 text-ink-400" />
        </div>
      </Screen>
    )
  }

  if (query.error) {
    return (
      <Screen>
        <Notice title="This link is not working">
          {errorMessage(query.error)} If you were expecting an invoice, reply to the email it came in and we
          will send it again.
        </Notice>
      </Screen>
    )
  }

  return <Invoice view={query.data} />
}

function Invoice({ view }: { view: View }) {
  const { state, business, invoice } = view
  const documentUrl = absolute(view.documentUrl)
  const contact = [business.phone, business.email].filter(Boolean).join(' or ')
  const noun = view.creditNote ? 'credit note' : 'invoice'

  const download = documentUrl ? (
    <a
      href={`${documentUrl}?download=1`}
      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[var(--p-brand)] px-5 text-sm font-bold text-[var(--p-on-brand)] shadow-sm"
    >
      <Icon name="download" className="size-4" />
      Download the PDF
    </a>
  ) : null

  if (state === 'VOID') {
    return (
      <Screen business={business}>
        <Notice title={`This ${noun} has been cancelled`}>
          {business.name} has cancelled it, so there is nothing to pay
          {contact ? `. If that is unexpected, contact us on ${contact}.` : '.'}
        </Notice>
      </Screen>
    )
  }

  if (state === 'REPLACED' || state === 'CHANGED' || !invoice) {
    return (
      <Screen business={business}>
        <Notice title={state === 'REPLACED' ? `There is a newer copy of this ${noun}` : `This ${noun} has been updated`}>
          {state === 'REPLACED'
            ? `${business.name} sent you this ${noun} again after this email. Please use the link in the most recent one`
            : `${business.name} has changed it since this email was sent, and will send you the new copy`}
          {contact ? `, or contact us on ${contact}.` : '.'}
        </Notice>
        {download ? <div className="px-4 sm:px-0">{download}</div> : null}
      </Screen>
    )
  }

  const due = invoice.facts.find((fact) => fact.label === 'Due')

  return (
    <Screen business={business}>
      {state === 'PAID' ? (
        <section
          role="status"
          className="mx-4 flex items-center gap-3 rounded-2xl bg-[var(--p-brand-soft)] px-5 py-4 font-proposal ring-1 ring-[var(--p-brand)] sm:mx-0"
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--p-brand)] text-[var(--p-on-brand)]">
            <Icon name="check" className="size-5" />
          </span>
          <p className="text-base font-bold text-ink-900">Paid — thank you</p>
        </section>
      ) : null}

      <article className="bg-white px-5 py-6 font-proposal shadow-sm sm:rounded-2xl sm:px-10 sm:py-10">
        <Letterhead business={business} />

        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-ink-900">{invoice.title}</h1>
            <p className="font-mono text-sm font-semibold text-ink-600">{invoice.number}</p>
          </div>
          <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm">
            {invoice.facts.map((fact) => (
              <div key={fact.label} className="contents">
                <dt className="text-ink-500">{fact.label}</dt>
                <dd className="text-right font-medium tabular-nums text-ink-900">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-bold tracking-[0.06em] text-ink-500 uppercase">Billed to</p>
            <p className="mt-1 font-semibold text-ink-900">{invoice.customer.name}</p>
            {invoice.customer.address ? (
              <p className="whitespace-pre-line text-ink-700">{invoice.customer.address}</p>
            ) : null}
          </div>
          {invoice.site ? (
            <div>
              <p className="text-[11px] font-bold tracking-[0.06em] text-ink-500 uppercase">Where</p>
              <p className="mt-1 whitespace-pre-line text-ink-700">{invoice.site}</p>
            </div>
          ) : null}
        </div>

        <table className="mt-8 w-full text-sm">
          <thead>
            <tr className="border-b border-ink-200 text-left text-[11px] font-bold tracking-[0.06em] text-ink-500 uppercase">
              <th className="py-2 font-bold">Item</th>
              <th className="hidden py-2 text-right font-bold sm:table-cell">Qty</th>
              <th className="hidden py-2 text-right font-bold sm:table-cell">Unit</th>
              <th className="py-2 text-right font-bold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => (
              <tr key={line.id} className="border-b border-ink-100 align-baseline">
                <td className="py-2.5 pr-3 text-ink-900">
                  {line.description}
                  <span className="block text-xs text-ink-500 sm:hidden">
                    {line.quantity} × {line.unitPrice}
                  </span>
                </td>
                <td className="hidden py-2.5 text-right tabular-nums text-ink-700 sm:table-cell">{line.quantity}</td>
                <td className="hidden py-2.5 text-right tabular-nums text-ink-700 sm:table-cell">{line.unitPrice}</td>
                <td className="py-2.5 text-right font-medium tabular-nums text-ink-900">{line.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <dl className="mt-4 ml-auto flex max-w-xs flex-col gap-1.5 text-sm">
          {invoice.totals.map((row) => (
            <div key={row.label} className="flex justify-between">
              <dt className="text-ink-500">{row.label}</dt>
              <dd className="tabular-nums text-ink-800">{row.value}</dd>
            </div>
          ))}
          <div className="flex justify-between border-t border-ink-200 pt-1.5 text-base font-bold text-ink-900">
            <dt>{invoice.grandTotal.label}</dt>
            <dd className="tabular-nums">{invoice.grandTotal.value}</dd>
          </div>
          {invoice.amountDue ? (
            <div className="flex justify-between text-base font-bold text-[var(--p-brand-text)]">
              <dt>{invoice.amountDue.label}</dt>
              <dd className="tabular-nums">{invoice.amountDue.value}</dd>
            </div>
          ) : null}
        </dl>

        {state === 'OPEN' && invoice.payment ? (
          <section className="mt-8 rounded-xl bg-ink-50 px-5 py-4 text-sm ring-1 ring-ink-200">
            <h2 className="font-bold text-ink-900">How to pay{due ? ` by ${due.value}` : ''}</h2>
            <p className="mt-1 whitespace-pre-line leading-relaxed text-ink-700">{invoice.payment}</p>
          </section>
        ) : null}

        {invoice.notes ? (
          <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-ink-700">{invoice.notes}</p>
        ) : null}
        {invoice.terms ? (
          <p className="mt-4 whitespace-pre-line text-xs leading-relaxed text-ink-500">{invoice.terms}</p>
        ) : null}

        {download ? <div className="mt-8">{download}</div> : null}
      </article>
    </Screen>
  )
}

function Screen({ business, children }: { business?: View['business']; children: React.ReactNode }) {
  return (
    <div style={themeVars(themeOf(business))} className="min-h-dvh bg-ink-100 sm:py-8">
      <main className="mx-auto flex max-w-3xl flex-col gap-4">{children}</main>
      {business ? (
        <footer className="mx-auto max-w-3xl px-5 py-8 text-center text-xs text-ink-500">
          {business.name}
          {business.taxNumber ? ` · ABN ${business.taxNumber}` : ''}
          {business.phone ? ` · ${business.phone}` : ''}
          {business.email ? ` · ${business.email}` : ''}
        </footer>
      ) : null}
    </div>
  )
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section role="status" className={cn('mx-4 rounded-xl bg-white px-5 py-4 text-sm text-ink-700 ring-1 ring-ink-200 sm:mx-0')}>
      <h1 className="text-base font-semibold">{title}</h1>
      <div className="mt-1 leading-relaxed">{children}</div>
    </section>
  )
}
